import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { TrendingDown, Clock, Truck, Target, BarChart3, ChevronRight, PackageCheck, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { supColor } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

function fmtCountdown(secs) {
  if (secs == null) return null;
  if (secs <= 0) return "Closed";
  const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60);
  const d = Math.floor(h / 24);
  if (d >= 1) return `${d}d ${h % 24}h`;
  return `${String(h).padStart(2, "0")}h ${String(m).padStart(2, "0")}m`;
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [deadlines, setDeadlines] = useState([]);
  const [basket, setBasket] = useState(null);
  const [products, setProducts] = useState([]);
  const [marginTarget, setMarginTarget] = useState(30);
  const isManager = user?.role === "owner" || user?.role === "manager";

  const load = async () => {
    try {
      const [s, dl, b] = await Promise.all([api.get("/dashboard/stats"), api.get("/suppliers/deadlines"), api.get("/basket")]);
      setStats(s.data); setDeadlines(dl.data); setBasket(b.data);
    } catch {
      setStats((p) => p || { savings_week_vs_pricier: 0, has_data: false });
      setDeadlines((p) => (p && p.length ? p : []));
      setBasket((p) => p || { groups: [] });
    }
    api.get("/settings/margin").then((r) => { setMarginTarget(r.data.margin_target); }).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!deadlines.length) return undefined;
    const t = setInterval(() => setDeadlines((ds) => ds.map((x) => ({ ...x, seconds_left: Math.max(0, x.seconds_left - 1) }))), 1000);
    return () => clearInterval(t);
  }, [deadlines.length]);

  const weekSavings = stats?.savings_week_vs_pricier ?? 0;
  const lowMargin = products.filter((p) => p.low_margin);
  const trackers = (basket?.groups || []).filter((g) => g.min_order > 0 || g.min_order_cases > 0);

  return (
    <div className="flex flex-col gap-3 fade-up pb-[130px] lg:pb-4 lg:grid lg:grid-cols-12 lg:gap-5" data-testid="dashboard-grid">
      <div className="lg:col-span-12">
        <h1 className="text-lg font-extrabold font-head text-slate-900 leading-tight" data-testid="home-title">Welcome back, {user?.store_name || "Store"}</h1>
        <p className="text-slate-500 text-xs">Wholesale Insights & Active Deadlines</p>
      </div>

      {/* Savings card — compact */}
      <motion.div data-testid="weekly-savings-banner" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
        className="lg:col-span-8 rounded-2xl px-4 py-3 lg:px-6 lg:py-6 text-white shadow-lg flex items-center justify-between" style={{ background: "linear-gradient(135deg,#1D4ED8 0%,#2563EB 55%,#3B82F6 100%)" }}>
        <div>
          <div className="flex items-center gap-1.5 text-blue-100 text-xs font-semibold"><TrendingDown className="w-3.5 h-3.5" /> Saved this week</div>
          <p className="text-blue-100 text-[10px]">{stats && !stats.has_data ? "No orders yet — savings appear after your first order" : "vs a pricier supplier"}</p>
        </div>
        <p data-testid="weekly-savings-amount" className="text-3xl lg:text-5xl font-extrabold font-mono tracking-tight">{stats ? `£${weekSavings.toFixed(2)}` : "—"}</p>
      </motion.div>

      {/* Smart Inventory Insights — dedicated full page */}
      <button data-testid="smart-insights-card" onClick={() => navigate("/inventory-insights")} className="order-last lg:order-none lg:col-span-4 lg:h-full w-full flex items-center gap-3 rounded-2xl px-4 py-3.5 text-white shadow-lg active:scale-[0.99] transition-all" style={{ background: "linear-gradient(135deg,#0F172A 0%,#1E293B 60%,#334155 100%)" }}>
        <span className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center"><Sparkles className="w-5 h-5" /></span>
        <div className="flex-1 text-left"><p className="font-bold text-sm">📊 Smart Inventory Insights</p><p className="text-[11px] text-slate-300">Movers, dead stock, re-orders & local best-sellers</p></div>
        <ChevronRight className="w-4 h-4 text-slate-400" />
      </button>

      {/* Cut-off deadlines — horizontal chips */}
      <div className="lg:col-span-12 lg:bg-white lg:border lg:border-slate-200 lg:rounded-2xl lg:p-4 lg:shadow-sm">
        <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-1.5">Cut-off Deadlines</p>
        <div className="flex gap-2 overflow-x-auto lg:flex-wrap pb-1" data-testid="cutoff-deadlines">
          {deadlines.length === 0 && <span data-testid="cutoff-empty" className="text-xs text-slate-400">No cut-offs yet — add supplier cut-off times in Supplier Vault.</span>}
          {deadlines.map((d, i) => {
            const urgent = d.seconds_left / 60 <= 60;
            return (
              <button key={`${d.name}-${i}`} data-testid={`cutoff-${d.name}`} onClick={() => navigate(`/suppliers?supplier=${encodeURIComponent(d.name)}`)}
                className={`shrink-0 flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full active:scale-95 transition-all ${urgent ? "bg-red-600 text-white" : "bg-blue-50 text-blue-700"}`}>
                <Clock className="w-3.5 h-3.5" /> {d.name} · {fmtCountdown(d.seconds_left)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Free delivery tracker — compact */}
      {trackers.length > 0 && (
        <div className={isManager ? "lg:col-span-8" : "lg:col-span-12"}>
          <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-1.5 flex items-center gap-1.5"><Truck className="w-3.5 h-3.5 text-emerald-600" /> Free Delivery Progress</p>
          <div className={`space-y-2 lg:space-y-0 lg:grid ${trackers.length > 1 ? "lg:grid-cols-2" : "lg:grid-cols-1"} lg:gap-3`} data-testid="free-delivery-tracker">
            {trackers.map((g) => (
              <button key={g.group} data-testid={`tracker-${g.group}`} onClick={() => navigate(`/suppliers?supplier=${encodeURIComponent(g.supplier)}`)} className="w-full text-left bg-white border border-slate-200 rounded-xl px-3 py-2 shadow-sm active:scale-[0.99] transition-all">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold" style={{ color: supColor(g.supplier) }}>{g.group}</span>
                  <span dir="ltr" className="text-[11px] text-slate-500 font-semibold">
                    {g.min_order > 0 ? `£${g.subtotal.toFixed(0)}/£${g.min_order.toFixed(0)}` : ""}
                    {g.min_order > 0 && g.min_order_cases > 0 ? " · " : ""}
                    {g.min_order_cases > 0 ? `${g.total_cases}/${g.min_order_cases} cases` : ""}
                  </span>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-1.5">
                  <div className="h-full transition-all" style={{ width: `${g.min_order > 0 ? Math.min(100, (g.subtotal / g.min_order) * 100) : Math.min(100, (g.total_cases / (g.min_order_cases || 1)) * 100)}%`, background: (g.remaining_for_free > 0 || g.remaining_cases > 0) ? supColor(g.supplier) : "#10B981" }} />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Margin controls — compact rows */}
      {isManager && (
        <div className={`space-y-2 lg:space-y-0 lg:grid lg:gap-3 ${trackers.length ? "lg:col-span-4 lg:grid-cols-1 lg:content-start lg:pt-6" : "lg:col-span-12 lg:grid-cols-2"}`}>
          <button data-testid="margin-target-card" onClick={() => navigate("/settings/margin")} className="w-full flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-3 py-2.5 shadow-sm active:scale-[0.99] transition-all">
            <span className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center"><Target className="w-4 h-4" /></span>
            <span className="flex-1 text-left text-sm font-semibold text-slate-800">Category POR Targets</span>
            <span dir="ltr" className="text-xs font-bold text-indigo-600">6 categories</span>
            <ChevronRight className="w-4 h-4 text-slate-300" />
          </button>
          <button data-testid="margin-report-card" onClick={() => navigate("/settings/report")} className="w-full flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-3 py-2.5 shadow-sm active:scale-[0.99] transition-all">
            <span className="w-8 h-8 rounded-lg bg-red-50 text-red-600 flex items-center justify-center"><BarChart3 className="w-4 h-4" /></span>
            <span className="flex-1 text-left text-sm font-semibold text-slate-800">Margin & Profit Report</span>
            <span className="text-lg font-extrabold font-mono text-slate-900">{lowMargin.length} <span className="text-[10px] font-sans font-medium text-slate-400">low</span></span>
            <ChevronRight className="w-4 h-4 text-slate-300" />
          </button>
        </div>
      )}
    </div>
  );
}
